import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import { useBootstrap } from '../ui/BootstrapProvider';
import DashboardRoute from '../pages/DashboardRoute';
import PlannerDashboardPage from '../pages/manual-dashboard/planner/PlannerDashboardPage';
import ContactPulsePage from '../pages/contact-optimization/PulsePage';
import ContactPlanPage from '../pages/contact-optimization/PlanPage';
import ContactTimingPage from '../pages/contact-optimization/TimingPage';
import ContactQueuePage from '../pages/contact-optimization/QueuePage';
import type { NavLink, NavSection } from './types';

function linkAt(sections: NavSection[], pathname: string): NavLink | null {
  for (const section of sections) {
    for (const item of section.items) {
      const links = item.kind === 'group' ? item.items : [item];
      const hit = links.find((link) => link.path === pathname && link.internal_path);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * A page opened at the address its sidebar row gives it —
 * /marketing/campaign-performance rather than /dashboard/<id>/campaigns, or
 * /sales/pulse rather than /monitoring/contact-optimization.
 *
 * The server hands each board row a readable `path` and the board's own
 * `internal_path` (backend/services/navigation/service.py). This draws the
 * board's usual page against that internal address, so the page, its
 * parameters and its chat are exactly what /dashboard/<id> would give; only
 * the address bar reads differently. An address no row claims goes to Copilot,
 * as any unknown address always has.
 */
export default function SidebarAddressPage() {
  const location = useLocation();
  const { navigation, loading } = useBootstrap();
  const link = React.useMemo(() => linkAt(navigation, location.pathname), [navigation, location.pathname]);

  if (!link) {
    if (loading) {
      return <Box sx={{ px: 0.5 }}><Skeleton variant="rounded" height={320} /></Box>;
    }
    return <Navigate to="/copilot" replace />;
  }
  return (
    <Routes location={{ ...location, pathname: link.internal_path! }}>
      <Route path="/dashboard/:dashboardId" element={<DashboardRoute />} />
      <Route path="/dashboard/:dashboardId/simulator" element={<PlannerDashboardPage key="simulator" view="simulator" />} />
      <Route path="/dashboard/:dashboardId/campaigns" element={<PlannerDashboardPage key="campaigns" view="campaigns" />} />
      {/* App pages a tenant's sidebar may give an address of its own (a row
          with a `slug`). Add a page here before giving its row a slug; an
          address with nothing to draw falls through to Copilot. */}
      <Route path="/monitoring/contact-optimization" element={<ContactPulsePage />} />
      <Route path="/monitoring/contact-optimization/plan" element={<ContactPlanPage />} />
      <Route path="/monitoring/contact-optimization/timing" element={<ContactTimingPage />} />
      <Route path="/monitoring/contact-optimization/queue" element={<ContactQueuePage />} />
      <Route path="*" element={<Navigate to="/copilot" replace />} />
    </Routes>
  );
}
